import React from "react";
import {
    Sparkles, Trash2, ChevronDown, ChevronRight, Loader2, Wrench, Layers, GitMerge, ArrowRightLeft,
} from "lucide-react";
import { Panel } from "@/components/common/Panel";
import { TiltCard } from "@/components/common/TiltCard";
import { SeverityBadge, FieldConfidenceBadge, CatalogCoverageBadge } from "@/components/common/Badges";
import { SkeletonRows } from "@/components/common/Skeleton";
import { TID } from "@/lib/testIds";
import { SuggestedControls } from "@/components/roles/SuggestedControls";
import { FunctionDrilldown } from "@/components/roles/FunctionDrilldown";

export function RoleLibraryPanel({
    roles, visibleRoles, loading, selected, toggleSelect, expanded, setExpanded,
    describing, aiDescribe, remove, openTools,
    verificationFilter, setVerificationFilter, verificationCoverage,
    runConsolidation, consolidating, setBulkEditorOpen,
    runMigrateAll, migratingAll,
}) {
    const tcodeOnlyCount = roles.filter((r) => (r.tcodes || []).length > 0 && (r.fiori_catalogs || []).length === 0).length;
    return (
        <Panel eyebrow="Role Library" title={`Engineered roles (${visibleRoles.length}/${roles.length})`} action={
            <div className="flex items-center gap-3 flex-wrap">
                {verificationCoverage && (
                    <span className="text-[11px] font-mono text-slate-500">
                        {verificationCoverage.verified}/{verificationCoverage.total} field-verified
                    </span>
                )}
                <div className="inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-sm p-0.5">
                    {[["all", "All"], ["unverified", "Needs Review"], ["verified", "Verified"]].map(([val, label]) => (
                        <button
                            key={val}
                            data-testid={`role-verification-filter-${val}`}
                            onClick={() => setVerificationFilter(val)}
                            className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wider transition-colors ${verificationFilter === val ? "bg-cyan-500/20 text-cyan-300" : "text-slate-500 hover:text-slate-300"}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {selected.size >= 1 && (
                    <button
                        data-testid="role-bulk-field-editor-btn"
                        onClick={() => setBulkEditorOpen(true)}
                        className="inline-flex items-center gap-1.5 text-xs font-mono text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                        <Layers size={13} /> Bulk Field Editor ({selected.size})
                    </button>
                )}
                {tcodeOnlyCount > 0 && (
                    <button
                        data-testid={TID.roleMigrateAllBtn}
                        onClick={runMigrateAll}
                        disabled={migratingAll}
                        title="Migrate every T-code-only role onto the smallest covering set of real Fiori Business Catalogs"
                        className="inline-flex items-center gap-1.5 text-xs font-mono text-cyan-400 hover:text-cyan-300 transition-colors disabled:opacity-40"
                    >
                        {migratingAll ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />}
                        Migrate All to Fiori ({tcodeOnlyCount})
                    </button>
                )}
                <button
                    data-testid={TID.roleConsolidateBtn}
                    onClick={runConsolidation}
                    disabled={consolidating || roles.length < 2}
                    className="inline-flex items-center gap-1.5 text-xs font-mono text-cyan-400 hover:text-cyan-300 transition-colors disabled:opacity-40"
                >
                    {consolidating ? <Loader2 size={13} className="animate-spin" /> : <GitMerge size={13} />}
                    Analyze consolidation
                </button>
            </div>
        }>
            {loading ? (
                <SkeletonRows rows={5} cols={5} />
            ) : visibleRoles.length === 0 ? (
                <p className="text-sm text-slate-500 font-mono py-6 text-center">
                    {roles.length === 0
                        ? "No roles yet — paste specifications above and generate."
                        : "No roles match this filter."}
                </p>
            ) : (
                <div className="space-y-2">
                    {visibleRoles.map((r) => {
                        const isOpen = expanded === r.id;
                        return (
                            <TiltCard key={r.id} max={3} testId={TID.roleRow(r.id)} className="group panel card-lift p-4 rounded-[4px]">
                                <div className="flex items-center gap-3">
                                    <input
                                        type="checkbox"
                                        data-testid={TID.roleSelectBox(r.id)}
                                        checked={selected.has(r.id)}
                                        onChange={() => toggleSelect(r.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-4 h-4 accent-cyan-400 cursor-pointer shrink-0"
                                        aria-label={`Select ${r.name} for simulation`}
                                    />
                                    <button
                                        onClick={() => setExpanded(isOpen ? null : r.id)}
                                        className="text-slate-500 hover:text-cyan-400 transition-colors"
                                        aria-label="Expand"
                                    >
                                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-sm text-white truncate">{r.name}</span>
                                            <SeverityBadge severity={r.risk_level} />
                                            <CatalogCoverageBadge role={r} testId={TID.roleCatalogCoverageBadge(r.id)} />
                                            {(r.fiori_catalogs || []).length === 0 && (
                                                <button
                                                    data-testid={TID.roleMigrateShortcutBtn(r.id)}
                                                    onClick={(e) => { e.stopPropagation(); openTools(r, "migration"); }}
                                                    title="One-click migrate this T-code role onto real Fiori Business Catalogs"
                                                    className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/20 hover:border-cyan-400/50 transition-colors"
                                                >
                                                    <ArrowRightLeft size={9} /> Migrate
                                                </button>
                                            )}
                                            {r.field_verified && (
                                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" title="Field/ACTVT values captured — SoD re-verified against real access">
                                                    field-verified
                                                </span>
                                            )}
                                            {r.sod_conflicts.length > 0 && (
                                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-rose-500/15 text-rose-300 border border-rose-500/30">
                                                    {r.sod_conflicts.length} SoD conflict{r.sod_conflicts.length > 1 ? "s" : ""}
                                                </span>
                                            )}
                                            {r.critical_access?.length > 0 && (
                                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-orange-500/15 text-orange-300 border border-orange-500/30">
                                                    {r.critical_access.length} critical access
                                                </span>
                                            )}
                                            {r.validation_issues.length > 0 && (
                                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                                    {r.validation_issues.length} validation
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] font-mono text-slate-500 mt-1 truncate">
                                            {r.tcodes.length} tcode(s): {r.tcodes.slice(0, 8).join(", ")}{r.tcodes.length > 8 ? "…" : ""}
                                        </div>
                                    </div>
                                    <button
                                        data-testid={TID.roleAiDescribeBtn(r.id)}
                                        onClick={() => aiDescribe(r.id)}
                                        disabled={describing === r.id}
                                        className="text-slate-500 hover:text-cyan-400 transition-colors"
                                        title="AI description"
                                    >
                                        {describing === r.id ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                                    </button>
                                    <button
                                        data-testid={TID.roleToolsBtn(r.id)}
                                        onClick={() => openTools(r)}
                                        className="text-slate-500 hover:text-cyan-400 transition-colors"
                                        title="Authorization Matrix Manager (org fields, simulation, .SAP export, BPD/PDD)"
                                    >
                                        <Wrench size={15} />
                                    </button>
                                    <button
                                        data-testid={TID.roleDeleteBtn(r.id)}
                                        onClick={() => remove(r.id)}
                                        className="text-slate-500 hover:text-rose-400 transition-colors"
                                        aria-label="Delete role"
                                    >
                                        <Trash2 size={15} />
                                    </button>
                                </div>

                                {isOpen && (
                                    <div className="mt-3 pt-3 border-t border-white/5 space-y-3">
                                        <div>
                                            <div className="label-eyebrow mb-1">Description</div>
                                            <p className="text-xs text-slate-300 leading-relaxed">{r.ai_description || r.description || "—"}</p>
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-3">
                                            <div>
                                                <div className="label-eyebrow mb-1">Inferred Auth Objects</div>
                                                <div className="flex flex-wrap gap-1">
                                                    {r.auth_objects.map((o) => (
                                                        <span key={o} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">{o}</span>
                                                    ))}
                                                </div>
                                            </div>
                                            {r.validation_issues.length > 0 && (
                                                <div>
                                                    <div className="label-eyebrow mb-1 text-amber-400">Validation Issues</div>
                                                    <ul className="text-[11px] text-amber-200/90 list-disc pl-4 space-y-0.5">
                                                        {r.validation_issues.map((v, i) => <li key={i}>{v}</li>)}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                        {r.sod_conflicts.length > 0 && (
                                            <div>
                                                <div className="label-eyebrow mb-1 text-rose-400">Segregation-of-Duties Conflicts</div>
                                                <div className="space-y-2">
                                                    {r.sod_conflicts.map((c) => (
                                                        <div key={c.rule_id} className="panel-elevated p-2.5 border-l-2 border-rose-500/50">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-mono text-[10px] text-rose-300">{c.rule_id}</span>
                                                                <SeverityBadge severity={c.severity} />
                                                                <span className="text-xs text-white font-medium">{c.rule_name}</span>
                                                                <FieldConfidenceBadge confidence={c.match_confidence} note={c.field_level_note} />
                                                                {(c.suggested_controls || []).length > 0 && (
                                                                    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" title="Mitigation proposals below">
                                                                        {c.suggested_controls.map((s) => s.control_id).join(" · ")}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-[11px] text-slate-400 mt-1">{c.description}</p>
                                                            <div className="text-[10px] font-mono text-slate-500 mt-1">
                                                                {c.function_a} [{c.matched_a.join(", ")}] ↔ {c.function_b} [{c.matched_b.join(", ")}]
                                                            </div>
                                                            <FunctionDrilldown
                                                                testId={`conflict-drilldown-${r.id}-${c.rule_id}`}
                                                                functions={[
                                                                    { name: c.function_a, matched: c.matched_a },
                                                                    { name: c.function_b, matched: c.matched_b },
                                                                ]}
                                                            />
                                                            <SuggestedControls controls={c.suggested_controls} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {r.critical_access?.length > 0 && (
                                            <div>
                                                <div className="label-eyebrow mb-1 text-orange-400">Standalone Critical Activities</div>
                                                <div className="space-y-2">
                                                    {r.critical_access.map((c) => (
                                                        <div key={c.risk_id} className="panel-elevated p-2.5 border-l-2 border-orange-500/50">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="font-mono text-[10px] text-orange-300">{c.risk_id}</span>
                                                                <SeverityBadge severity={c.severity} />
                                                                <span className="text-xs text-white font-medium">{c.name}</span>
                                                                <FieldConfidenceBadge confidence={c.match_confidence} note={c.field_level_note} />
                                                            </div>
                                                            <div className="text-[10px] font-mono text-slate-500 mt-1">
                                                                {c.function} [{c.matched.join(", ")}]
                                                            </div>
                                                            <FunctionDrilldown
                                                                testId={`critical-drilldown-${r.id}-${c.risk_id}`}
                                                                functions={[{ name: c.function, matched: c.matched }]}
                                                            />
                                                            <SuggestedControls controls={c.suggested_controls} />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </TiltCard>
                        );
                    })}
                </div>
            )}
        </Panel>
    );
}
